import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  fetchAvailableServiceTimes as fetchAvailableServiceTimesRequest,
  fetchCalendlyLink as fetchCalendlyLinkRequest,
} from 'src/services/client';
import { IAvailableServiceTimeDay } from 'src/services/api/UserManagement/apiContracts';

export interface FetchCalendlyLinkResult {
  ok: boolean;
  schedulingUrl: string;
}

export interface UseServiceRequestSchedulingAPIReturn {
  availableServiceTimes: IAvailableServiceTimeDay[];
  fetchCalendlyLink: (email: string) => Promise<FetchCalendlyLinkResult>;
  fetchAvailableServiceTimes: (
    serviceProviderId: string,
    servicePreferences: string[]
  ) => Promise<void>;
}

const TOAST_OPTIONS = {
  autoClose: 12000,
  position: 'top-center' as const,
};

const isOkResponse = (response: any): boolean =>
  response.ok || response.status === 200;

export const useServiceRequestSchedulingAPI =
  (): UseServiceRequestSchedulingAPIReturn => {
    const [availableServiceTimes, setAvailableServiceTimes] = useState<
      IAvailableServiceTimeDay[]
    >([]);

    const fetchCalendlyLink = async (
      email: string
    ): Promise<FetchCalendlyLinkResult> => {
      try {
        const response: any = await fetchCalendlyLinkRequest(email);

        if (!isOkResponse(response)) {
          throw new Error(`Failed to fetch calendar link: ${response.status}`);
        }

        const payload: any = await response.json();

        // legacy/workaround: BE wraps the result in a second statusCode, and a
        // provider only counts as Calendly-enabled when both calendarLink and
        // calendlyAPIKey are present. No link just means manual scheduling.
        const isCalendlyEnabled: boolean = Boolean(
          payload?.statusCode === 200 &&
            payload?.data?.calendarLink &&
            payload?.data?.calendlyAPIKey
        );

        return {
          ok: true,
          schedulingUrl: isCalendlyEnabled ? payload.data.calendarLink : '',
        };
      } catch (err: any) {
        console.error(
          'useServiceRequestSchedulingAPI fetchCalendlyLink failed:',
          err
        );

        return { ok: false, schedulingUrl: '' };
      }
    };

    const fetchAvailableServiceTimes = async (
      serviceProviderId: string,
      servicePreferencesParams: string[]
    ): Promise<void> => {
      // todo: fix root cause legacy/workaround: Rize and Formwerks providers only offer one fixed
      // weekly window, so BE expects this hardcoded preference instead of the
      // user's selection.
      const theme: string | null = localStorage.getItem('theme');

      const servicePreferences: string[] | string =
        theme === 'Rize' || theme === 'Formwerks'
          ? 'Monday:Mornings'
          : servicePreferencesParams;

      try {
        const response: any = await fetchAvailableServiceTimesRequest(
          serviceProviderId,
          servicePreferences
        );

        if (!isOkResponse(response)) {
          throw new Error(
            `Failed to fetch service time availability: ${response.status}`
          );
        }

        const payload: IAvailableServiceTimeDay[] = await response.json();

        setAvailableServiceTimes(payload);
      } catch (err: any) {
        console.error(
          'useServiceRequestSchedulingAPI fetchAvailableServiceTimes failed:',
          err
        );
        toast.error(
          ' 🚫 Not able to fetch service time availability!',
          TOAST_OPTIONS
        );
      }
    };

    return {
      availableServiceTimes,
      fetchCalendlyLink,
      fetchAvailableServiceTimes,
    };
  };